import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, ShoppingCart, Armchair, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import FullWidthHero from '../components/FullWidthHero';
import ContactCTA from '../components/ContactCTA';
import CatalogSearchBar from '../components/CatalogSearchBar';
import HelpBlock from '../components/HelpBlock';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPrice } from '../utils/formatPrice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "contact@atlantiquespaboutique.com";

// Helper to add to cart
const addToCart = (product) => {
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: 'accessoires'
    });
  }
  
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
  
};

const AccessoiresSpaPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState(null); // null = no search active

  // Handle search results callback
  const handleSearchResults = (results) => {
    setSearchResults(results);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/parts`);
        // Filter for accessoires
        const accessoires = response.data.filter(p => 
          p.main_category === 'accessoires' ||
          p.sub_category?.includes('escalier') ||
          p.sub_category?.includes('couverture') ||
          p.sub_category?.includes('leve-couverture') ||
          p.sub_category?.includes('appui-tete') ||
          p.sub_category?.includes('porte-verre')
        );
        setProducts(accessoires);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // JSON-LD for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Accessoires pour spa",
    "description": "Accessoires de confort pour spa : escaliers, lève-couvertures, couvertures isothermes, appuie-têtes. Livraison France.",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://atlantiquespaboutique.com/" },
        { "@type": "ListItem", "position": 2, "name": "Pièces & Accessoires", "item": "https://atlantiquespaboutique.com/pieces-accessoires" },
        { "@type": "ListItem", "position": 3, "name": "Accessoires", "item": "https://atlantiquespaboutique.com/accessoires-spa" }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="accessoires-spa-page">
      <SEO 
        title="Accessoires pour spa – Confort et équipements complémentaires | Atlantique Spa Boutique"
        description="Accessoires de confort pour spa : escaliers, lève-couvertures, couvertures isothermes, appuie-têtes. Améliorez votre expérience spa. Livraison France."
        canonical="/accessoires-spa"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/pieces-accessoires">Pièces & Accessoires</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Accessoires</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Hero Header - Full Width Image */}
      <FullWidthHero
        variant="compact"
        image={realPhotos.installation1}
        imageAlt={realPhotosAlt.installation1}
        badge="Confort & praticité"
        badgeIcon={Armchair}
        title="Accessoires pour spa"
        subtitle="Améliorez le confort et la praticité de votre spa avec nos accessoires. Escaliers, lève-couvertures, couvertures isothermes, appuie-têtes... Tout pour une expérience spa optimale."
        overlayPosition="left"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        
        {/* SEO Intro Text */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <p className="text-slate-600 leading-relaxed">
            Complétez votre installation spa avec nos <strong>accessoires de qualité</strong>. 
            Un escalier facilite l'accès à votre spa, un lève-couverture simplifie la manipulation quotidienne. 
            Nous avons sélectionné des accessoires pratiques et durables pour améliorer votre confort au quotidien.
          </p>
        </div>

        {/* Unified Search Bar - Full Width Enhanced */}
        <div className="mb-6">
          <CatalogSearchBar 
            products={products}
            onSearchResults={handleSearchResults}
            placeholder="Rechercher une pièce ou une référence (ex : pompe LX WP250, clavier Balboa, filtre PRB50)"
            category="accessoires"
            emptyIcon={Armchair}
            showHelpText={true}
          />
        </div>

        {/* Help Block - Moved below search */}
        <div className="mb-6">
          <HelpBlock />
        </div>

        {/* Search Results Indicator */}
        {searchResults !== null && (
          <div className="mb-6 flex items-center justify-between bg-[#1E3A5F]/5 rounded-lg px-4 py-3">
            <p className="text-slate-700">
              <span className="font-semibold">{searchResults.length}</span> résultat{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''}
            </p>
            <button 
              onClick={() => setSearchResults(null)}
              className="text-sm text-[#1E3A5F] font-medium hover:underline"
            >
              Effacer la recherche
            </button>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="h-40 bg-slate-200 rounded-lg mb-4" />
                <div className="h-5 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (searchResults !== null ? searchResults : products).length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <Armchair className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {searchResults !== null ? 'Aucun accessoire trouvé' : 'Accessoires bientôt disponibles'}
            </h3>
            <p className="text-slate-600 mb-6">
              {searchResults !== null 
                ? 'Essayez avec une autre référence ou mot-clé.'
                : 'Nous préparons notre catalogue d\'accessoires.'
              }
            </p>
            <Link to="/contact">
              <Button className="bg-[#1E3A5F] hover:bg-[#152C4A]">
                Nous contacter pour un accessoire
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
            {(searchResults !== null ? searchResults : products).map((product) => (
              <Card key={product.slug} className="overflow-hidden border border-slate-200 hover:border-[#1E3A5F]/30 hover:shadow-lg transition-all group" data-testid={`product-${product.slug}`}>
                <Link to={`/pieces-accessoires/${product.slug}`}>
                  <div className="relative h-40 bg-slate-100 flex items-center justify-center">
                    {product.images?.[0] ? (
                      <img 
                        src={product.images[0]} 
                        alt={product.name}
                        className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <Armchair className="w-12 h-12 text-slate-300" />
                    )}
                    {product.availability === 'en_stock' && (
                      <span className="absolute top-2 right-2 bg-emerald-500 text-white text-xs font-medium px-2 py-1 rounded">
                        En stock
                      </span>
                    )}
                  </div>
                </Link>
                <CardContent className="p-4">
                  <span className="text-xs text-slate-500 uppercase tracking-wide">{product.brand}</span>
                  <Link to={`/pieces-accessoires/${product.slug}`}>
                    <h3 className="font-semibold text-slate-900 text-sm mt-1 mb-2 line-clamp-2 group-hover:text-[#1E3A5F] transition-colors">
                      {product.name}
                    </h3>
                  </Link>
                  {product.price_ttc ? (
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-slate-900">{formatPrice(product.price_ttc)}</span>
                      <Button 
                        size="sm" 
                        onClick={() => addToCart(product)}
                        className="bg-[#1E3A5F] hover:bg-[#152C4A] h-8 px-3"
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Link to="/contact">
                      <Button variant="outline" size="sm" className="w-full">
                        Demander un prix
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* SEO Bottom Text */}
        <div className="mt-12 bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Améliorez votre expérience spa</h2>
          <div className="text-slate-600">
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">Confort au quotidien</h3>
              <p className="text-sm leading-relaxed">
                Un escalier adapté facilite l'entrée et la sortie du spa en toute sécurité. 
                Les appuie-têtes ajoutent un confort supplémentaire pour vos moments de détente.
                Les lève-couvertures permettent de manipuler facilement votre couverture sans effort.
              </p>
            </div>
          </div>
        </div>

        {/* Cross-links to other categories */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Link to="/pieces-detachees-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Pièces détachées</span>
              <p className="text-xs text-slate-500">Pompes, réchauffeurs...</p>
            </div>
          </Link>
          <Link to="/entretien-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Entretien de spa</span>
              <p className="text-xs text-slate-500">Filtres, produits...</p>
            </div>
          </Link>
          <Link to="/pompes-a-chaleur-spa" className="bg-white rounded-xl border border-slate-200 p-4 hover:border-[#1E3A5F]/30 hover:shadow-md transition-all flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ArrowRight className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-sm">Pompes à chaleur</span>
              <p className="text-xs text-slate-500">3 kW, 5 kW, 7 kW</p>
            </div>
          </Link>
        </div>

        {/* CTA pièce spécifique */}
        <ContactCTA category="Accessoires spa" />

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Besoin de conseils sur les accessoires ?
          </h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Notre équipe vous aide à choisir les accessoires adaptés à votre spa
          </p>
          <a href={`mailto:${EMAIL}`}>
            <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold gap-2">
              <Mail className="w-5 h-5" />
              Nous contacter
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default AccessoiresSpaPage;
